import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import PoojaAddonsModal from './PoojaAddonsModal';
import { resolvePunditImageList, resolvePunditImageUrl } from '../utils/imageUrl';

const Card = ({ item, showAddToCart = false }) => {
  const [currentImg, setCurrentImg] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showConfigurator, setShowConfigurator] = useState(false);
  const [checkoutSettings, setCheckoutSettings] = useState({ paymentGatewayEnabled: false });

  const isPooja = item.type === 'Pooja';
  const tiers = item.pricing_tiers || [];
  const [selectedTier, setSelectedTier] = useState(isPooja ? tiers[0] : null);

  const images = item.images?.length
    ? resolvePunditImageList(item.images)
    : [resolvePunditImageUrl(item.image_url)].filter(Boolean);
  const displayPrice = isPooja && selectedTier ? Number(selectedTier.price || 0) : Number(item.price || 0);
  const originalPrice = isPooja && selectedTier
    ? Number(selectedTier.originalPrice ?? selectedTier.price ?? 0)
    : Number(item.originalPrice ?? item.price ?? 0);
  const hasDiscount = Boolean((isPooja ? selectedTier?.discountActive : item.discountActive)
    && originalPrice > displayPrice);
  const showOriginalPrice = originalPrice > 0 && (hasDiscount || (isPooja && !checkoutSettings.paymentGatewayEnabled));

  useEffect(() => {
    if (!isPooja) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/checkout-settings`)
      .then((res) => setCheckoutSettings({
        paymentGatewayEnabled: Boolean(res.data?.paymentGatewayEnabled),
      }))
      .catch((err) => {
        console.error('Unable to load checkout settings', err);
        setCheckoutSettings({ paymentGatewayEnabled: false });
      });
  }, [isPooja]);

  const handleAddToCart = () => {
    if (isPooja) {
      setShowConfigurator(true);
    }
  };

  return (
    <>
      <motion.div className="pp-card inline-variant">
        <div className="image-container" onClick={() => setIsFullScreen(true)}>
          {images[0] && <img src={images[currentImg]} alt={item.name} />}
          {images.length > 1 && (
            <div className="carousel-controls">
              <button onClick={(e) => { e.stopPropagation(); setCurrentImg(prev => prev === 0 ? images.length - 1 : prev - 1); }}>Prev</button>
              <button onClick={(e) => { e.stopPropagation(); setCurrentImg(prev => prev === images.length - 1 ? 0 : prev + 1); }}>Next</button>
            </div>
          )}
        </div>

        <div className="pp-card-body">
          <span className="category-tag">{item.type}</span>
          <div className="card-header-row">
            <h4>{item.name}</h4>
            {isPooja && (
              <Link to={`/uc/pooja-details/${item.id}`} className="view-details-link">
                View Details
              </Link>
            )}
          </div>

          {isPooja && (
            <>
              <div className="detailed-specs">
                <div className="spec-group">
                  <h6>Pundit</h6>
                  <p>{item.included?.pundit_count || 0} Verified Pundit</p>
                </div>
                <div className="spec-group">
                  <h6>Included Items</h6>
                  <ul className="items-mini-list">
                    {(item.included?.items || []).map((includedItem, i) => <li key={i}>- {includedItem}</li>)}
                  </ul>
                </div>
              </div>

              {tiers.length > 0 && (
                <div className="inline-tier-selector">
                  <h6>Select Duration</h6>
                  <div className="tier-chips">
                    {tiers.map(t => (
                      <button key={t.id} className={`tier-chip ${selectedTier?.id === t.id ? 'active' : ''}`} onClick={() => setSelectedTier(t)}>
                        {t.duration}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="card-footer">
            <div className="price-wrapper">
              {showOriginalPrice && (
                <span className="struck-price">${originalPrice.toFixed(2)}</span>
              )}
              <p className="price">{isPooja && !checkoutSettings.paymentGatewayEnabled ? 'Free booking today' : `$${displayPrice.toFixed(2)}`}</p>
              {hasDiscount && <small className="discount-badge">{(isPooja ? selectedTier?.discountLabel : item.discountLabel) || 'Limited-time offer'}</small>}
            </div>

            {showAddToCart && (
              <div className="action-area">
                <button className="card-add-btn" onClick={handleAddToCart} disabled={isPooja && !selectedTier}>
                  Add to Cart
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {isFullScreen && images[0] && (
          <motion.div className="lightbox-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button className="lightbox-close" onClick={() => setIsFullScreen(false)}>Close</button>
            <div className="lightbox-content">
              <img src={images[currentImg]} alt="Full view" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <PoojaAddonsModal
        open={showConfigurator}
        onClose={() => setShowConfigurator(false)}
        pooja={item}
        tier={selectedTier}
        images={images}
      />
    </>
  );
};

export default Card;
